import { yupResolver } from '@hookform/resolvers/yup';
import { type IDIALOGVERIFYTRANSACTIONPROPS, ModalParts, StageBeneficiary } from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	type CURRENCYCODETYPE,
	emptyBeneficiaryState,
	getFiatWithdrawFee,
	type ITransactionDetailsRow,
} from 'BreetHelpers';
import { useCurrencyOutput, useFormStage, useModalApi, useUserCurrencyProps, useUserWalletApi } from 'BreetHooks';
import { selectMeta, selectSelectedBeneficiary, useAppSelector, useGetBeneficiariesQuery } from 'BreetRedux';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import FormComponent from './components/FormComponent';
import TransactionDetails from './components/TransactionDetails';
import {
	CreateWithdrawRequestFormSchema,
	requestPaymentStageTitle,
	type WITHDRAWREQUESTSTAGETYPE,
} from './DialogWithdrawRequest.helpers';

const DialogWithdrawRequest = () => {
	const { openModalDirect } = useModalApi();

	const { data: beneficiariesData } = useGetBeneficiariesQuery(undefined, alwaysRefetchApiQueryOptions);

	const meta = useAppSelector(selectMeta);

	const beneficiaries = beneficiariesData?.data ?? [];

	const beneficiary = useAppSelector(selectSelectedBeneficiary);

	const defaultSelectedBeneficiary =
		beneficiaries.length > 1
			? emptyBeneficiaryState
			: {
					accountName: beneficiary?.accountName ?? '',
					accountNumber: beneficiary?.accountNumber ?? '',
					bankName: beneficiary?.bankName ?? '',
					id: beneficiary?._id ?? '',
				};

	const defaultNarration = beneficiaries.length > 1 ? '' : (beneficiary?.narration ?? '');

	const currencyOutput = useCurrencyOutput();

	const currencyProps = useUserCurrencyProps();

	const userWalletProps = useUserWalletApi(currencyProps?.code);

	const withdrawalFee = getFiatWithdrawFee(meta?.settings, currencyProps?.code);

	const formStage = useFormStage<WITHDRAWREQUESTSTAGETYPE>(
		'withdraw-funds',
		['withdraw-funds', 'add-beneficiary', 'withdraw-details', 'manage-beneficiaries'],
		requestPaymentStageTitle
	);

	const { navigator, isStageActive } = formStage;

	const withdrawRequestHookForm = useForm({
		resolver: yupResolver(CreateWithdrawRequestFormSchema),
		defaultValues: {
			amount: '',
			selectedBeneficiary: defaultSelectedBeneficiary,
			narration: defaultNarration,
		},
		mode: 'onChange',
	});

	useEffect(() => {
		withdrawRequestHookForm.setValue('selectedBeneficiary', defaultSelectedBeneficiary);
		withdrawRequestHookForm.setValue('narration', defaultNarration);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultSelectedBeneficiary.id]);

	const { watch: withdrawRequestWatch } = withdrawRequestHookForm;

	const selectedBeneficiary = withdrawRequestWatch('selectedBeneficiary');

	const amount = withdrawRequestWatch('amount');
	const narration = withdrawRequestWatch('narration');

	const withdrawDetailsContent: ITransactionDetailsRow[] = [
		{
			label: 'Amount',
			value: `${userWalletProps.symbol} ${currencyOutput(amount)}`,
		},
		{
			label: 'Bank Account',
			bankDetails: {
				accountName: selectedBeneficiary.accountName,
				accountNumber: selectedBeneficiary.accountNumber,
				bankName: selectedBeneficiary.bankName,
			},
		},
	];

	const amountNum = isNaN(Number(amount)) ? 0 : Number(amount);
	const totalDeducted = withdrawalFee + amountNum;

	const withdrawFunds = useCallback(() => {
		const metadata = {
			transactionType: 'withdraw-fiat',
			withdrawFiatReqData: {
				amount: Number(amount) || 0,
				bankId: selectedBeneficiary.id,
				narration,
			},
		} as IDIALOGVERIFYTRANSACTIONPROPS;

		openModalDirect({
			id: 'verifyTransaction',
			metadata,
			variant: 'right-sheet',
		});
	}, [amount, narration, openModalDirect, selectedBeneficiary.id]);

	return (
		<StageBeneficiary
			formStage={formStage}
			manageBeneficaryStage='manage-beneficiaries'
			beneficiaryStage='add-beneficiary'
			rootStage='withdraw-funds'
			stageTitleObj={requestPaymentStageTitle}
		>
			<ModalParts.Stage hide={!isStageActive('withdraw-funds')}>
				<FormComponent
					onNext={navigator.goTo('withdraw-details')}
					withdrawRequestHookForm={withdrawRequestHookForm}
					onAddNewBeneficiary={navigator.goTo('add-beneficiary')}
					onManageBeneficiaries={navigator.goTo('manage-beneficiaries')}
					currencyProps={currencyProps}
					userWalletProps={userWalletProps}
					meta={meta}
				/>
			</ModalParts.Stage>

			<ModalParts.Stage hide={!isStageActive('withdraw-details')}>
				<TransactionDetails
					content={withdrawDetailsContent}
					balance={userWalletProps.currencyAmount}
					withdrawalFee={`${userWalletProps.symbol} ${currencyOutput(withdrawalFee)}`}
					narration={narration}
					code={currencyProps?.code as unknown as CURRENCYCODETYPE}
					totalDeducted={`${userWalletProps.symbol} ${currencyOutput(totalDeducted)}`}
					isInsufficientFunds={totalDeducted > userWalletProps.walletBalance}
					withdrawFunds={withdrawFunds}
				/>
			</ModalParts.Stage>
		</StageBeneficiary>
	);
};

export default DialogWithdrawRequest;
